/**
 * Status enum -> localized label mapping.
 *
 * The DB / wire value stays English (e.g. `disposition_status === "active"`);
 * only the rendered label is translated. See `i18n/GLOSSARY.md`.
 */
import { useTranslation } from 'react-i18next'

export type StatusKind = 'call' | 'campaignContact' | 'simulationEntry'

const NAMESPACE_BY_KIND: Record<StatusKind, string> = {
  call: 'calls',
  campaignContact: 'campaigns',
  simulationEntry: 'simulations',
}

const KEY_PREFIX_BY_KIND: Record<StatusKind, string> = {
  call: 'status',
  campaignContact: 'contactStatus',
  simulationEntry: 'entryStatus',
}

/**
 * Returns the translated label for a given status. Unknown statuses fall back
 * to the raw wire value so the UI never silently swallows a value it doesn't
 * recognize.
 */
export function useStatusLabel(status: string, kind: StatusKind = 'call'): string {
  const ns = NAMESPACE_BY_KIND[kind]
  const prefix = KEY_PREFIX_BY_KIND[kind]
  const { t } = useTranslation(ns)
  const key = `${prefix}.${status}`
  const translated = t(key, { defaultValue: status })
  return translated
}

export function useDirectionLabel(direction: string): string {
  const { t } = useTranslation('calls')
  return t(`direction.${direction}`, { defaultValue: direction })
}
